import { Button } from '@ai-tutor/ui';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { CheckoutFlow } from '@/components/features/billing/forms/CheckoutFlow';
import { PlanPicker } from '@/components/features/billing/picker/PlanPicker';
import { usePlans, useStartTrial } from '@/hooks/account/useBilling';
import type { Plan } from '@/lib/api-client';
import { groupPlans, TRIAL_ENABLED } from '@/lib/billing';
import { track } from '@/config/observability';
import { notify } from '@/lib/notify';

import { StepLayout } from '../StepLayout';

interface PricingStepProps {
  /** Called once the user has made a choice (Free, trial, or paid). */
  onFinish: () => void;
  onBack: () => void;
  /** Called with the plan bought, just before `onFinish`, after a payment. */
  onSubscribed?: (plan: Plan) => void;
}

/**
 * Display stand-ins for a catalog row that is missing — Stripe not configured
 * yet, so there is no paid plan, or no free row. They keep the step's two
 * cards on screen (Free must always be offered); the paid stand-in is never
 * purchasable, its Subscribe renders disabled. A FAILED catalog fetch does
 * not use these — see `PlansUnavailable`.
 */
const FALLBACK_FREE: Plan = {
  id: 'fallback-free',
  tier: 'free',
  name: 'Free',
  amount_cents: 0,
  currency: 'usd',
  interval: 'month',
  promo: null,
  sort_order: 0,
  features: [
    '10 AI lessons per month',
    'Mind maps, quizzes & flashcards',
    'AI tutor chat & practice',
    'Up to 5 sources per lesson',
    'Redo any lesson once with a new prompt',
    "Podcast of each lesson's first section, 10 a month",
  ],
};

const FALLBACK_PLUS: Plan = {
  id: 'fallback-plus',
  tier: 'paid',
  name: 'Plus',
  amount_cents: 1499,
  currency: 'usd',
  interval: 'month',
  promo: null,
  sort_order: 1,
  features: [
    'Everything in Free, plus:',
    'Unlimited AI lessons',
    'Fine-tune lessons (up to 6 versions)',
    'Up to 20 sources per lesson (4× more)',
    'Unlimited AI tutor chat & practice',
    'A podcast for every section of a lesson',
  ],
};

function withDisplayFallbacks(plans: Plan[]): Plan[] {
  const grouped = groupPlans(plans);
  return [
    ...(grouped.free ? [] : [FALLBACK_FREE]),
    ...plans,
    ...(grouped.monthly || grouped.annual ? [] : [FALLBACK_PLUS]),
  ];
}

/**
 * Two-plan pricing: Free or Plus, with a monthly/yearly toggle — the shared
 * `PlanPicker`, with this step's own in-card actions. Free-default and never
 * blocks — not even when the catalog fails to load, which is what
 * `PlansUnavailable` below is for. Paid checkout reuses `CheckoutFlow`.
 */
export function PricingStep({
  onFinish,
  onBack,
  onSubscribed,
}: PricingStepProps) {
  const plansQuery = usePlans();
  const startTrial = useStartTrial();
  const plans = useMemo(
    () => withDisplayFallbacks(plansQuery.data ?? []),
    [plansQuery.data]
  );
  const [checkoutPlanId, setCheckoutPlanId] = useState<string | null>(null);

  const handleStartTrial = () => {
    startTrial.mutate(undefined, {
      onSuccess: () => {
        track('trial_started', { surface: 'onboarding' });
        notify.success('Your free trial is active. Enjoy!');
        onFinish();
      },
      onError: err => notify.error(err, 'Couldn’t start your trial'),
    });
  };

  // Embedded checkout for the chosen paid interval.
  if (checkoutPlanId) {
    return (
      <CheckoutFlow
        initialPlanId={checkoutPlanId}
        onBack={() => setCheckoutPlanId(null)}
        onSuccess={plan => {
          onSubscribed?.(plan);
          onFinish();
        }}
      />
    );
  }

  return (
    <StepLayout
      title='Choose your plan'
      description='Start free, no card needed. Upgrade anytime.'
      align='center'
      back={
        <Button
          type='button'
          variant='ghost'
          onClick={onBack}
          disabled={startTrial.isPending}
        >
          <ArrowLeft className='mr-1 h-4 w-4' />
          Back
        </Button>
      }
    >
      {plansQuery.isLoading ? (
        <div className='flex items-center justify-center rounded-lg border border-border p-10'>
          <Loader2 className='h-5 w-5 animate-spin text-muted-foreground' />
        </div>
      ) : plansQuery.isError ? (
        <PlansUnavailable
          retrying={plansQuery.isFetching}
          onRetry={() => plansQuery.refetch()}
          onFinish={onFinish}
        />
      ) : (
        <PlanPicker
          plans={plans}
          initialInterval='year'
          renderFooter={plan => (
            <PricingFooter
              plan={plan}
              trialPending={startTrial.isPending}
              onFinish={onFinish}
              onSubscribe={planId => {
                const chosen = plans.find(p => p.id === planId) ?? plan;
                track('plan_selected', {
                  plan_id: chosen.id,
                  interval: chosen.interval,
                  amount_cents: chosen.amount_cents,
                });
                setCheckoutPlanId(planId);
              }}
              onStartTrial={handleStartTrial}
            />
          )}
        />
      )}
    </StepLayout>
  );
}

/**
 * The catalog failed to load.
 *
 * Everything on the paid card is catalog-driven, and the fallbacks underneath
 * it are display defaults, not real data. Rendering them on a failed fetch put
 * a hardcoded "$14.99/mo" on screen as though the server had quoted it, beside
 * a Subscribe button that could never work. A wrong price is worse than no
 * price, so both cards are replaced outright — but Free still finishes
 * onboarding from here, because a broken catalog must never trap a user in the
 * wizard.
 */
function PlansUnavailable({
  retrying,
  onRetry,
  onFinish,
}: {
  retrying: boolean;
  onRetry: () => void;
  onFinish: () => void;
}) {
  return (
    <div className='flex flex-col items-center gap-3 rounded-lg border border-border p-10 text-center'>
      <AlertCircle aria-hidden='true' className='h-5 w-5 text-destructive' />
      <p className='max-w-sm text-sm text-muted-foreground'>
        We couldn’t load our plans right now. You can start on the free plan and
        upgrade any time from Settings.
      </p>
      {/* `tap-target` on both: drawn as 32px `sm` buttons, tapped as 44 on a
          phone - this is the only way forward when the plans fail to load. */}
      <div className='[&>button]:tap-target mt-1 flex items-center gap-2'>
        <Button
          type='button'
          variant='outline'
          size='sm'
          onClick={onRetry}
          disabled={retrying}
        >
          {retrying ? (
            <>
              <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
              Retrying…
            </>
          ) : (
            'Try again'
          )}
        </Button>
        <Button type='button' size='sm' onClick={onFinish}>
          Start with Free
        </Button>
      </div>
    </div>
  );
}

/** Each card's action: Free finishes the step, Plus opens checkout. */
function PricingFooter({
  plan,
  trialPending,
  onFinish,
  onSubscribe,
  onStartTrial,
}: {
  plan: Plan;
  trialPending: boolean;
  onFinish: () => void;
  onSubscribe: (planId: string) => void;
  onStartTrial: () => void;
}) {
  if (plan.tier !== 'free') {
    return (
      <PaidActions
        plan={plan.id === FALLBACK_PLUS.id ? undefined : plan}
        trialPending={trialPending}
        onSubscribe={onSubscribe}
        onStartTrial={onStartTrial}
      />
    );
  }
  // "Start with Free", not "Continue". Both cards sit in parallel positions,
  // so parallel verbs matter — and step 1's Continue is a NEXT-STEP button,
  // which made this one read as "skip" rather than as choosing a plan.
  return (
    <Button
      type='button'
      variant='outline'
      className='tap-target w-full'
      onClick={onFinish}
    >
      Start with Free
    </Button>
  );
}

/**
 * The paid card's actions. `plan` is undefined for the display stand-in (no
 * paid plan in the catalog): then the no-card trial is the only CTA, or —
 * with the trial off — a disabled Subscribe.
 */
function PaidActions({
  plan,
  trialPending,
  onSubscribe,
  onStartTrial,
}: {
  plan: Plan | undefined;
  trialPending: boolean;
  onSubscribe: (planId: string) => void;
  onStartTrial: () => void;
}) {
  if (!plan) {
    if (!TRIAL_ENABLED) {
      return (
        <Button type='button' className='tap-target w-full' disabled>
          Subscribe
        </Button>
      );
    }
    return (
      <Button
        type='button'
        className='tap-target w-full'
        onClick={onStartTrial}
        disabled={trialPending}
      >
        {trialPending ? (
          <>
            <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
            Starting…
          </>
        ) : (
          'Start 7-day free trial'
        )}
      </Button>
    );
  }
  return (
    <div className='space-y-2'>
      <Button
        type='button'
        className='tap-target w-full'
        onClick={() => onSubscribe(plan.id)}
      >
        Subscribe
      </Button>
      {/* Trial hidden behind TRIAL_ENABLED until it's tested. */}
      {TRIAL_ENABLED && (
        <button
          type='button'
          onClick={onStartTrial}
          disabled={trialPending}
          className='flex w-full items-center justify-center gap-1.5 text-center text-label text-brand-600 hover:text-brand-700 disabled:opacity-60 dark:text-brand-400 dark:hover:text-brand-300'
        >
          {trialPending && <Loader2 className='h-3.5 w-3.5 animate-spin' />}
          or start 7-day free trial
        </button>
      )}
    </div>
  );
}
